import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { HttpError, createQueryKeys } from "@nasam/kernel";
import type { BulkPreviewOutcome } from "../../../shared/bulkUpload";
import { requestError } from "../../../shared/utils/requestError";
import { saveFile } from "../../../shared/utils/saveFile";
import type {
  CreateGeofenceInput,
  GeofenceExportQuery,
  GeofenceBulkRow,
  GeofencesRepository,
  UpdateGeofenceInput,
} from "../domain";

export const geofencesQueryKeys = createQueryKeys("fleet-geofences");

/** The list loads this many geofences at a time, the next as it's scrolled to. */
export const GEOFENCE_PAGE_SIZE = 10;

/** The geofence list, page after page; `search` is matched against the name. */
export function useGeofencesListQuery(
  repository: GeofencesRepository,
  search: string,
) {
  return useInfiniteQuery({
    queryKey: geofencesQueryKeys.list({ search }),
    queryFn: ({ pageParam }) =>
      repository.list({ page: pageParam, limit: GEOFENCE_PAGE_SIZE, search }),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.meta.nextPageUrl === null ? undefined : last.meta.currentPage + 1,
  });
}

/** Every geofence the search matches, for the map layer. */
export function useGeofencesMapQuery(
  repository: GeofencesRepository,
  search: string,
) {
  return useQuery({
    queryKey: [...geofencesQueryKeys.all, "map", { search }],
    queryFn: () => repository.listForMap({ search }),
  });
}

export function useGeofenceQuery(repository: GeofencesRepository, id: string) {
  return useQuery({
    queryKey: geofencesQueryKeys.detail(id),
    queryFn: () => repository.getById(id),
  });
}

interface GeofenceSaveMessages {
  readonly failed: string;
  readonly duplicate: string;
}

/** A name already used, in the user's language (Create Scenario 5, Edit
 *  Scenario 4). Shown by the app-wide mutation error toast; the form keeps its
 *  values for a fix or retry. */
function geofenceSaveError(
  error: unknown,
  messages: GeofenceSaveMessages,
): unknown {
  if (error instanceof HttpError && error.status === 409) {
    return new Error(messages.duplicate, { cause: error });
  }
  return requestError(error, messages.failed);
}

export function useCreateGeofenceMutation(
  repository: GeofencesRepository,
  messages: GeofenceSaveMessages,
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateGeofenceInput) => {
      try {
        return await repository.create(input);
      } catch (error) {
        throw geofenceSaveError(error, messages);
      }
    },
    // The list and the map load the new geofence.
    onSuccess: () =>
      client.invalidateQueries({ queryKey: geofencesQueryKeys.all }),
  });
}

export function useUpdateGeofenceMutation(
  repository: GeofencesRepository,
  messages: GeofenceSaveMessages,
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      input,
    }: {
      readonly id: string;
      readonly input: UpdateGeofenceInput;
    }) => {
      try {
        return await repository.update(id, input);
      } catch (error) {
        throw geofenceSaveError(error, messages);
      }
    },
    // The list, the map and this geofence's form show the change.
    onSuccess: () =>
      client.invalidateQueries({ queryKey: geofencesQueryKeys.all }),
  });
}

export const GEOFENCE_TEMPLATE_FILE_NAME = "geofence-bulk-upload-template.xlsx";

/** The name the API gives the export (its `Content-Disposition`). */
export const GEOFENCE_EXPORT_FILE_NAME = "geofences.xlsx";

/** Download on the geofence list: every geofence it shows, as an Excel file,
 *  fetched with the user's token and saved. */
export function useExportGeofencesMutation(
  repository: GeofencesRepository,
  failureMessage: string,
) {
  return useMutation({
    mutationFn: async (query: GeofenceExportQuery) => {
      let file: Blob;
      try {
        file = await repository.exportGeofences(query);
      } catch (error) {
        // As the template's: the error body is a file, so its message can't
        // be read.
        throw new Error(failureMessage, { cause: error });
      }
      saveFile(file, GEOFENCE_EXPORT_FILE_NAME);
    },
  });
}

/** Download Template (Bulk Upload, Scenario 2). */
export function useDownloadGeofenceTemplateMutation(
  repository: GeofencesRepository,
  failureMessage: string,
) {
  return useMutation({
    mutationFn: async () => {
      let file: Blob;
      try {
        file = await repository.downloadTemplate();
      } catch (error) {
        // A file request's error body is a file too, so the server's message
        // can't be read: always `failureMessage`, in the app-wide toast.
        throw new Error(failureMessage, { cause: error });
      }
      saveFile(file, GEOFENCE_TEMPLATE_FILE_NAME);
    },
  });
}

/** Check (Bulk Upload): sends the file to be read and validated; nothing is
 *  created. A refusal comes back as an outcome, not an error, so it shows on
 *  the form instead of in the app-wide toast. */
export function usePreviewGeofenceBulkUploadMutation(
  repository: GeofencesRepository,
  failureMessage: string,
) {
  return useMutation({
    mutationFn: async (
      file: File,
    ): Promise<BulkPreviewOutcome<GeofenceBulkRow>> => {
      try {
        return {
          kind: "checked",
          preview: await repository.previewBulkUpload(file),
        };
      } catch (error) {
        const explained = requestError(error, failureMessage);
        if (explained instanceof HttpError) {
          return { kind: "refused", message: explained.message };
        }
        throw explained;
      }
    },
  });
}

/** Confirm (Bulk Upload, Scenario 22): imports the same file's valid rows. */
export function useBulkUploadGeofencesMutation(
  repository: GeofencesRepository,
  failureMessage: string,
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      try {
        return await repository.bulkUpload(file);
      } catch (error) {
        // Shown by the app-wide mutation error toast; the dialog stays open.
        throw requestError(error, failureMessage);
      }
    },
    // The new geofences join the list and the map (Scenarios 25 and 26).
    onSuccess: () =>
      client.invalidateQueries({ queryKey: geofencesQueryKeys.all }),
  });
}

export const GEOFENCE_BULK_ERROR_REPORT_FILE_NAME =
  "geofence-bulk-upload-errors.xlsx";

/** Download Error Report (Bulk Upload, Scenario 21): sends the same file again
 *  and saves its refused rows. */
export function useDownloadGeofenceBulkErrorReportMutation(
  repository: GeofencesRepository,
  failureMessage: string,
) {
  return useMutation({
    mutationFn: async (file: File) => {
      let report: Blob;
      try {
        report = await repository.downloadBulkErrorReport(file);
      } catch (error) {
        throw new Error(failureMessage, { cause: error });
      }
      saveFile(report, GEOFENCE_BULK_ERROR_REPORT_FILE_NAME);
    },
  });
}

export function useDeleteGeofenceMutation(
  repository: GeofencesRepository,
  failureMessage: string,
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      try {
        return await repository.delete(id);
      } catch (error) {
        // Shown by the app-wide mutation error toast; the dialog stays open
        // for a retry.
        throw requestError(error, failureMessage);
      }
    },
    // The geofence leaves the list and the map.
    onSuccess: () =>
      client.invalidateQueries({ queryKey: geofencesQueryKeys.all }),
  });
}
